/**
 * BlogEditor — Full-page rich blog editor (add & edit modes)
 *
 * Features:
 *  - Full-screen two-column layout: controls (left) + live preview (right)
 *  - ReactQuill with full toolbar (headers, bold/italic/u/strike, lists,
 *    blockquote, code, link, image, video, align, color, background, indent,
 *    script super/sub, clean)
 *  - Custom image handler: opens a popover to upload from disk OR paste URL
 *    (uploads to /api/upload?type=blog, inserts URL into Quill)
 *  - Cover image: URL input + upload button (same backend endpoint)
 *  - Live HTML preview in the right pane
 *  - Auto-save draft to localStorage every 30 s
 */

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";
import { insertBlog, updateBlog } from "../../../api/blogsAPI";
import { ImgWithFallback, placeholderImage, isValidUrl } from "../../invalidImage";

const BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

// ── Upload helper ─────────────────────────────────────────────
async function uploadImageFile(file) {
  const token = localStorage.getItem("accessToken") || "";
  const form = new FormData();
  form.append("image", file);
  const res = await fetch(`${BASE}/api/upload?type=blog`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) throw new Error("Upload gagal");
  const data = await res.json();
  return data.url;
}

// ── Quill toolbar config ──────────────────────────────────────
const quillFormats = [
  "header", "font", "size",
  "bold", "italic", "underline", "strike",
  "blockquote", "code-block",
  "list", "bullet", "indent",
  "align",
  "color", "background",
  "script",
  "link", "image", "video",
  "clean",
];

// ── Inline Image Upload Popover ───────────────────────────────
function ImageUploadPopover({ onInsert, onClose }) {
  const [tab, setTab] = useState("upload"); // "upload" | "url"
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const dropRef = useRef(null);

  const onFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith("image/")) {
      setFile(f);
      setPreviewUrl(URL.createObjectURL(f));
    }
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(30);
    try {
      setProgress(60);
      const url = await uploadImageFile(file);
      setProgress(100);
      onInsert(url);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload gagal", text: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleUrlInsert = () => {
    if (!urlInput.trim()) return;
    onInsert(urlInput.trim());
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-oswald text-lg font-semibold text-slate-900">
            Sisipkan Gambar
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100">
          {[
            { id: "upload", label: "⬆️ Upload File" },
            { id: "url", label: "🔗 URL Gambar" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-3 text-sm font-semibold transition-all ${
                tab === t.id
                  ? "text-brand-600 border-b-2 border-brand-600 bg-brand-50/50"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === "upload" ? (
            <div className="space-y-4">
              {/* Dropzone */}
              <div
                ref={dropRef}
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                className="relative border-2 border-dashed border-slate-200 hover:border-brand-400 rounded-xl p-8 text-center cursor-pointer transition-all bg-slate-50 hover:bg-brand-50/30"
                onClick={() => document.getElementById("blog-img-file-input").click()}
              >
                <input
                  id="blog-img-file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onFileChange}
                />
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="preview"
                    className="max-h-40 mx-auto rounded-lg object-contain"
                  />
                ) : (
                  <>
                    <div className="text-4xl mb-2">🖼️</div>
                    <p className="text-sm text-slate-500 font-overpass">
                      Drag & drop atau <span className="text-brand-600 font-semibold">klik untuk pilih</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      PNG, JPG, WebP — maks 5MB
                    </p>
                  </>
                )}
              </div>

              {/* Progress bar */}
              {uploading && (
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 bg-gradient-to-r from-brand-500 to-eco-500 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold text-sm disabled:opacity-50 hover:-translate-y-0.5 transition-all shadow-lg shadow-brand-500/30"
                >
                  {uploading ? "Mengupload..." : "⬆️ Upload & Sisipkan"}
                </button>
                {file && (
                  <button
                    type="button"
                    onClick={() => { setFile(null); setPreviewUrl(""); setProgress(0); }}
                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-all"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5 font-overpass">
                  URL Gambar
                </label>
                <input
                  type="text"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleUrlInsert()}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all"
                />
              </div>
              {urlInput && isValidUrl(urlInput) && (
                <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                  <img
                    src={urlInput}
                    alt="preview"
                    className="max-h-40 w-full object-contain"
                    onError={(e) => { e.target.src = placeholderImage; }}
                  />
                </div>
              )}
              <button
                type="button"
                onClick={handleUrlInsert}
                disabled={!urlInput.trim()}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold text-sm disabled:opacity-50 hover:-translate-y-0.5 transition-all shadow-lg shadow-brand-500/30"
              >
                Sisipkan Gambar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Cover Image Picker ────────────────────────────────────────
function CoverImagePicker({ imageUrl, onChange }) {
  const [uploadingCover, setUploadingCover] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadImageFile(file);
      onChange(url);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload gagal", text: err.message });
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={imageUrl}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... atau upload file →"
          className="flex-1 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingCover}
          className="shrink-0 px-4 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white text-sm font-semibold shadow-lg shadow-brand-500/20 hover:-translate-y-0.5 transition-all disabled:opacity-60"
        >
          {uploadingCover ? "⏳" : "⬆️ Upload"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-[16/6]">
        <ImgWithFallback
          src={imageUrl}
          alt="Cover preview"
          className="w-full h-full object-cover"
        />
        {!imageUrl && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
            <div className="text-3xl mb-1">🖼️</div>
            <p className="text-xs font-overpass">Preview gambar cover</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main BlogEditor Component ─────────────────────────────────
const DRAFT_KEY = "defra_blog_editor_draft";

const BlogEditor = ({ blog = null, onSuccess, onCancel }) => {
  const isEdit = Boolean(blog);

  const [title, setTitle]       = useState(blog?.title || "");
  const [author, setAuthor]     = useState(blog?.author || "Admin");
  const [imageUrl, setImageUrl] = useState(blog?.image || "");
  const [content, setContent]   = useState(blog?.description || "");
  const [saving, setSaving]     = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [imgPopover, setImgPopover]   = useState(false);
  const [hasDraft, setHasDraft]       = useState(false);

  const quillRef = useRef(null);

  // ── Auto-save draft ──────────────────────────────────────
  useEffect(() => {
    if (isEdit) return; // don't draft on edit
    const id = setInterval(() => {
      if (title || content) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, author, imageUrl, content }));
      }
    }, 30_000);
    return () => clearInterval(id);
  }, [title, author, imageUrl, content, isEdit]);

  // Check for existing draft on mount (add mode only)
  useEffect(() => {
    if (isEdit) return;
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw) {
      try {
        const d = JSON.parse(raw);
        if (d.title || d.content) setHasDraft(true);
      } catch {}
    }
  }, [isEdit]);

  const loadDraft = () => {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      setTitle(d.title || "");
      setAuthor(d.author || "Admin");
      setImageUrl(d.imageUrl || "");
      setContent(d.content || "");
      setHasDraft(false);
    } catch {}
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
  };

  // ── Quill image handler — opens our popover ──────────────
  const openImgPopover = useCallback(() => setImgPopover(true), []);

  const insertImageToEditor = useCallback((url) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true);
    quill.insertEmbed(range?.index ?? 0, "image", url, "user");
    quill.setSelection((range?.index ?? 0) + 1, 0);
    setImgPopover(false);
  }, []);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: {
        image: openImgPopover,
      },
    },
    clipboard: { matchVisual: false },
  }), [openImgPopover]);

  // ── Submit ───────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const stripped = content.replace(/<[^>]*>/g, "").trim();
    if (!title.trim() || !stripped || !author.trim()) {
      Swal.fire({ icon: "error", title: "Form tidak lengkap", text: "Judul, konten, dan author wajib diisi." });
      return;
    }

    const confirm = isEdit
      ? await Swal.fire({
          icon: "question",
          title: "Update blog?",
          text: "Perubahan akan langsung tersimpan.",
          showCancelButton: true,
          confirmButtonText: "Ya, update!",
          cancelButtonText: "Batal",
        })
      : { isConfirmed: true };

    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      Swal.fire({ title: isEdit ? "Menyimpan..." : "Menambahkan...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const payload = { title: title.trim(), image: imageUrl, description: content, author: author.trim() };
      if (isEdit) {
        await updateBlog(blog.id, payload);
      } else {
        await insertBlog(payload);
        clearDraft();
      }
      Swal.fire({ icon: "success", title: "Berhasil!", text: isEdit ? "Blog berhasil diupdate." : "Blog berhasil ditambahkan." })
        .then(() => { if (onSuccess) onSuccess(); });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Gagal", text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const previewHtml = DOMPurify.sanitize(content);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
            >
              ← Kembali
            </button>
            <span className="text-slate-300">/</span>
            <span className="text-sm font-semibold text-slate-700 font-overpass">
              {isEdit ? `Edit: ${(blog.title || "").slice(0, 40)}` : "Blog Baru"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Draft controls (add mode only) */}
            {!isEdit && hasDraft && (
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs text-amber-600 font-overpass font-semibold bg-amber-50 px-2 py-1 rounded-lg border border-amber-200">
                  📝 Draft tersimpan
                </span>
                <button type="button" onClick={loadDraft} className="text-xs text-brand-600 font-semibold hover:underline">
                  Muat
                </button>
                <button type="button" onClick={clearDraft} className="text-xs text-slate-400 hover:text-red-500 font-semibold">
                  Buang
                </button>
              </div>
            )}

            {/* Preview toggle */}
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                showPreview
                  ? "bg-brand-50 text-brand-700 border-brand-200"
                  : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
              }`}
            >
              👁️ {showPreview ? "Sembunyikan Preview" : "Tampilkan Preview"}
            </button>

            <button
              type="submit"
              form="blog-editor-form"
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold text-sm shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {saving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Menyimpan...
                </>
              ) : (
                <>{isEdit ? "💾 Update Blog" : "✨ Publikasikan"}</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className={`max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 ${showPreview ? "grid grid-cols-1 lg:grid-cols-2 gap-6" : ""}`}>
        {/* ── Left: Edit Form ── */}
        <form id="blog-editor-form" onSubmit={handleSubmit} className="space-y-5">
          {/* Title */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 font-overpass">
              Judul Artikel <span className="text-red-500">*</span>
            </label>
            <textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Tulis judul yang menarik..."
              rows={2}
              className="w-full px-0 py-1 text-2xl font-oswald font-semibold text-slate-900 bg-transparent border-none outline-none resize-none placeholder:text-slate-300 leading-snug"
            />
            <div className="flex items-center justify-between mt-1 text-xs text-slate-400 font-overpass">
              <span>{title.length} karakter</span>
              <span className={title.length > 200 ? "text-red-500 font-semibold" : ""}>
                {title.length > 200 ? "⚠️ Terlalu panjang" : ""}
              </span>
            </div>
          </div>

          {/* Author */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 font-overpass">
              Author <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Nama penulis"
              className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all"
            />
          </div>

          {/* Cover Image */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 font-overpass">
              Gambar Cover
            </label>
            <CoverImagePicker imageUrl={imageUrl} onChange={setImageUrl} />
          </div>

          {/* Content Editor */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-overpass">
                Konten Artikel <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-slate-400 font-overpass">
                Klik ikon 🖼️ di toolbar untuk sisipkan gambar
              </span>
            </div>
            <div className="quill-editor-wrap">
              <ReactQuill
                ref={quillRef}
                value={content}
                onChange={setContent}
                theme="snow"
                modules={quillModules}
                formats={quillFormats}
                placeholder="Tulis konten artikel di sini..."
                className="blog-quill"
              />
            </div>
          </div>

          {/* Mobile publish button (hidden on desktop — topbar has it) */}
          <div className="sm:hidden pt-2">
            <button
              type="submit"
              disabled={saving}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold shadow-lg shadow-brand-500/30 transition-all hover:-translate-y-0.5 disabled:opacity-60"
            >
              {isEdit ? "💾 Update Blog" : "✨ Publikasikan"}
            </button>
          </div>
        </form>

        {/* ── Right: Live Preview ── */}
        {showPreview && (
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-overpass">
                    Live Preview
                  </span>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(100vh-10rem)]">
                  {/* Cover */}
                  {imageUrl && (
                    <div className="rounded-xl overflow-hidden mb-5 aspect-[16/7] bg-slate-100">
                      <ImgWithFallback
                        src={imageUrl}
                        alt="cover"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  {/* Title */}
                  <h1 className="font-oswald text-2xl font-semibold text-slate-900 mb-2 leading-snug">
                    {title || <span className="text-slate-300">Judul artikel...</span>}
                  </h1>
                  <p className="text-xs text-slate-500 font-overpass mb-5">
                    oleh <strong>{author}</strong>
                  </p>
                  {/* Body */}
                  <div
                    className="blog-content text-slate-700 font-overpass text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                  {!content && (
                    <p className="text-slate-300 text-sm font-overpass italic">
                      Konten akan muncul di sini...
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Image Upload Popover (modal) */}
      {imgPopover && (
        <ImageUploadPopover
          onInsert={insertImageToEditor}
          onClose={() => setImgPopover(false)}
        />
      )}
    </div>
  );
};

export default BlogEditor;
